import React, { useEffect, useRef, useState } from "react";

export const ValidationContext = React.createContext();

export const ValidationProvider = props => {

    const errorVisible = useRef(false);
    const validationsRef = useRef(new Map());
    const invalidGroupsMapRef = useRef(new Map());
    const invalidInputsMapRef = useRef(new Map());

    const [invalidGroups, setInvalidGroups] = useState([]);
    const [invalidInputs, setInvalidInputs] = useState([]);

    useEffect(() => {
        validationsRef.current = getValidations(validationsRef.current);
    });

    const setValidation = (inputs, func, group) => {
        const names = inputs.map(input => input.name);
        const namesJson = JSON.stringify(names);
        validationsRef.current.set(namesJson, { inputs, func, group });
    };

    const getValidations = () => {
        // clean up unmounted inputs
        const newMap = new Map();
        validationsRef.current.forEach((validation, inputNames) => {
            validation.inputs.forEach(input => {
                if (input.ref.current !== null) {
                    newMap.set(inputNames, validation);
                }
            });
        });
        return newMap;
    };

    const setErrorsVisible = value => {
        errorVisible.current = value;
        getValidations().forEach(validation => validation.inputs.forEach(input => input.setErrorVisible(value)));
    };

    const validate = async (validation, errVisible) => {
        const validationValues = validation.inputs.map(input => (input.ref.current !== null ? input.ref.current.value : input.value));
        const result = await Promise.resolve(validation.func(...validationValues));
        const [input] = validation.inputs;
        let err = false;
        if (result === false) { // just show an error without message
            input.setError(true);
            err = true;
        } else if (result !== true) { // show an error with message
            input.setError(result);
            err = true;
        } else if (result === true) { // no error at all
            input.setError(false);
            err = false;
        }
        if (errVisible) {
            setErrorsVisible(true);
        }
        if (errorVisible.current === true) {
            const namesArray = validation.inputs.map(input => input.name);
            const namesArrayJson = JSON.stringify(namesArray);
            if (err) {
                invalidGroupsMapRef.current.set(namesArrayJson, validation.group);
                validation.inputs.forEach(input => invalidInputsMapRef.current.set(input.name, input));
            } else {
                invalidGroupsMapRef.current.delete(namesArrayJson);
                validation.inputs.forEach(input => invalidInputsMapRef.current.delete(input.name, input));
            }
            setInvalidInputs(Array.from(invalidInputsMapRef.current.keys()));
            setInvalidGroups(Array.from(new Set(invalidGroupsMapRef.current.values())));
        }

        return result;
    };

    const isValid = async (input, errVisible = true) => {
        const {name} = input;
        const validations = getValidations();
        const promises = [];
        for (const [key, validation] of validations.entries()) {
            const namesKey = JSON.parse(key);
            if (namesKey.includes(name)) {
                promises.push(validate(validation, errVisible));
            }
        }
        const results = await Promise.all(promises);
        const allValid = results.every(result => result === true);
        return allValid;
    };

    const allValid = async (errorVisible = true) => {
        // a full run re-evaluates every registered validation, so start from a clean slate -
        // otherwise a group of a meanwhile unmounted form would stay flagged
        invalidGroupsMapRef.current.clear();
        invalidInputsMapRef.current.clear();
        const validations = getValidations();
        const promises = Array.from(validations.values())
            .map(validation => validate(validation, errorVisible));
        const results = await Promise.all(promises);
        const allValid = results.every(result => result === true);
        return allValid;
    };

    const listenGroup = group => invalidGroups.includes(group);
    const listenInput = input => invalidInputs.includes(input.name);
    const getInvalidGroups = () => Array.from(new Set(invalidGroupsMapRef.current.values())).filter(group => group);

    return (
        <ValidationContext.Provider value={{
            isValid,
            allValid,
            setValidation,
            listenGroup,
            listenInput,
            getInvalidGroups,
            setErrorsVisible: setErrorsVisible,
        }}>
            {props.children}
        </ValidationContext.Provider>
    );
};
